import { useEffect, useMemo, useState } from "react";
import { Search, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";

const ROLE_OPTS = ["portal_admin", "provider", "customer"];

export function UsersManager() {
  const { t } = useI18n();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/control/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const s = q.toLowerCase();
    return users.filter((u) => !s || (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s));
  }, [users, q]);

  const patch = async (u, m, body) => {
    setBusy(u.id + m.portal_key);
    try {
      await api.patch("/control/memberships", { user_id: u.id, portal_key: m.portal_key, ...body });
      toast.success(t("common.saved"));
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!users) return <Loader />;

  return (
    <div className="kw-fade">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="font-heading text-3xl font-bold" style={{ color: "var(--text)" }}>{t("control.userMgmt")}</h1>
        <span className="text-sm" style={{ color: "var(--text-muted)" }}>{filtered.length}</span>
      </div>
      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
        <input data-testid="users-search" placeholder={t("control.searchUser")} value={q} onChange={(e) => setQ(e.target.value)}
          className="w-full pl-9 pr-3 h-11 text-sm border outline-none" style={st} />
      </div>
      <div className="space-y-3" data-testid="users-list">
        {filtered.map((u) => (
          <Card key={u.id} className="p-4 kw-fade-up" data-testid={`user-row-${u.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>
                <UserCog className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate" style={{ color: "var(--text)" }}>{u.name}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{u.email}</div>
              </div>
              {u.global_role && (
                <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>
                  {t(`roles.${u.global_role}`)}
                </span>
              )}
            </div>
            {u.memberships?.length > 0 && (
              <div className="mt-3 space-y-2">
                {u.memberships.map((m) => {
                  const active = (m.status || "active") === "active";
                  const rowBusy = busy === u.id + m.portal_key;
                  return (
                    <div key={m.portal_key} className="flex flex-wrap items-center gap-2" data-testid={`membership-${u.id}-${m.portal_key}`}>
                      <span className="text-xs uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{m.portal_key}</span>
                      <select value={m.role} disabled={rowBusy} onChange={(e) => patch(u, m, { role: e.target.value })}
                        data-testid={`role-select-${u.id}-${m.portal_key}`} className="px-2 h-9 text-sm border outline-none" style={st}>
                        {ROLE_OPTS.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
                      </select>
                      <span className="text-xs px-2 py-1 rounded-full" style={{ background: active ? "#DCFCE7" : "#FEE2E2", color: active ? "#15803D" : "#B91C1C" }}>{m.status || "active"}</span>
                      <Btn variant="ghost" onClick={() => patch(u, m, { status: active ? "suspended" : "active" })} disabled={rowBusy} className="h-9 px-3 text-xs" data-testid={`toggle-status-${u.id}-${m.portal_key}`}>
                        {active ? t("control.suspend") : t("control.activate")}
                      </Btn>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
